import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';

interface PayGrade {
  id: number;
  name: string;
  currency: string;
  minimum_amount: string | null;
  midpoint_amount: string | null;
  maximum_amount: string | null;
}

interface PayGradeStep {
  id: number;
  step_number: number;
  step_name: string | null;
  salary_amount: string;
}

interface StepForm {
  step_number: number | string;
  step_name: string;
  salary_amount: number | string;
}

const emptyForm: StepForm = { step_number: '', step_name: '', salary_amount: '' };

const TABS = ['details', 'steps'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = { details: 'Details', steps: 'Steps' };

export function PayGradeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [tab, setTab] = useState<Tab>('details');
  const [payGrade, setPayGrade] = useState<PayGrade | null>(null);
  const [steps, setSteps] = useState<PayGradeStep[] | null>(null);
  const [editing, setEditing] = useState<'new' | PayGradeStep | null>(null);
  const [form, setForm] = useState<StepForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const loadSteps = () => api.get<PayGradeStep[]>(`/pay-grades/${id}/steps`).then((res) => setSteps(res.data));

  useEffect(() => {
    api.get<PayGrade[]>('/pay-grades').then((res) => setPayGrade(res.data.find((g) => g.id === Number(id)) ?? null));
    loadSteps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fmtMoney = (value: string | null) => (value === null ? '—' : formatMoney(Number(value), payGrade?.currency ?? 'VUV'));

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (step: PayGradeStep) => {
    setForm({ step_number: step.step_number, step_name: step.step_name ?? '', salary_amount: step.salary_amount });
    setErrors({});
    setEditing(step);
  };

  const set = (key: keyof StepForm) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post(`/pay-grades/${id}/steps`, form);
      else if (editing) await api.put(`/pay-grade-steps/${editing.id}`, form);
      setEditing(null);
      loadSteps();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (step: PayGradeStep) => {
    if (!(await confirmDelete(`Delete step ${step.step_number}?`))) return;
    try {
      await api.delete(`/pay-grade-steps/${step.id}`);
      loadSteps();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!payGrade) return null;

  return (
    <>
      <header className="page-header">
        <h1>{payGrade.name}</h1>
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/system/pay-grades')} />
        </div>
      </header>

      <div className="toolbar">
        <div className="tab-group">
          {TABS.map((t) => (
            <button key={t} type="button" className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
        {tab === 'steps' && (
          <button type="button" className="btn btn-primary" onClick={openNew}>
            + Add
          </button>
        )}
      </div>

      <div className="card">
        {tab === 'details' && (
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-item-label">Name</span>
              <span className="detail-item-value">{payGrade.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Currency</span>
              <span className="detail-item-value">{payGrade.currency}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Minimum</span>
              <span className="detail-item-value">{fmtMoney(payGrade.minimum_amount)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Midpoint</span>
              <span className="detail-item-value">{fmtMoney(payGrade.midpoint_amount)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Maximum</span>
              <span className="detail-item-value">{fmtMoney(payGrade.maximum_amount)}</span>
            </div>
          </div>
        )}

        {tab === 'steps' && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Name</th>
                  <th>Salary</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {steps?.map((step) => (
                  <tr key={step.id}>
                    <td className="muted">{step.step_number}</td>
                    <td>{step.step_name ?? '—'}</td>
                    <td>{fmtMoney(step.salary_amount)}</td>
                    <td className="right">
                      <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(step)} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(step)} />
                    </td>
                  </tr>
                ))}
                {steps?.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted center">
                      No steps yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add step' : 'Edit step'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Step Number *
              <input type="number" min="1" value={form.step_number} onChange={set('step_number')} required />
              {errors.step_number && <small className="error">{errors.step_number[0]}</small>}
            </label>
            <label>
              Step Name
              <input value={form.step_name} onChange={set('step_name')} />
              {errors.step_name && <small className="error">{errors.step_name[0]}</small>}
            </label>
            <label className="span-2">
              Salary Amount *
              <input type="number" step="0.01" min="0" value={form.salary_amount} onChange={set('salary_amount')} required />
              {errors.salary_amount && <small className="error">{errors.salary_amount[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
